import React, { useEffect, useRef, useState } from 'react';
import { CharCount } from "./CharCount";
import { motion, AnimatePresence } from 'motion/react';
import { X, Mic, Feather, BookMarked, Send, Quote } from 'lucide-react';
import bookWhispersTranslations from '../i18n/bookWhispersTranslations';
import { useAuth } from '../context/AuthContext';
import '../styles/BookWhispers.css';

// BCP-47 codes for SpeechRecognition per app language
const SPEECH_LANG = {
  en: 'en-US', hi: 'hi-IN', te: 'te-IN', ta: 'ta-IN', kn: 'kn-IN',
  mr: 'mr-IN', bn: 'bn-IN', gu: 'gu-IN', ml: 'ml-IN', pa: 'pa-IN',
};

const MAX_COVER_BYTES = 2 * 1024 * 1024; // 2MB

export default function BookWhispersSection({ activeLang = 'en', backendUrl }) {
  const t = bookWhispersTranslations[activeLang] || bookWhispersTranslations.en;
  const { isAuthenticated, authHeaders } = useAuth();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState('form'); // 'form' | 'sealing' | 'done'

  // Form fields
  const [bookTitle, setBookTitle] = useState('');
  const [reflection, setReflection] = useState('');
  const [name, setName] = useState('');
  const [coverFile, setCoverFile] = useState(null); // { name, dataUrl }
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Voice recognition
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const interimRef = useRef('');
  const baseTextRef = useRef('');

  // Detect Web Speech API support
  const SpeechRecognition =
    typeof window !== 'undefined'
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null;

  // Lock body scroll when modal is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Stop recognition on close / unmount
  useEffect(() => {
    return () => {
      try { recognitionRef.current?.stop(); } catch { /* ignored */ }
    };
  }, []);

  const resetState = () => {
    setBookTitle('');
    setReflection('');
    setName('');
    setCoverFile(null);
    setError('');
    setPhase('form');
    setListening(false);
    try { recognitionRef.current?.stop(); } catch { /* ignored */ }
  };

  const closeModal = () => {
    setOpen(false);
    setTimeout(resetState, 400);
  };

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_COVER_BYTES) {
      setError(t.modal.uploadTooLarge);
      e.target.value = '';
      return;
    }
    setError('');
    const reader = new FileReader();
    reader.onload = () => setCoverFile({ name: file.name, dataUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const toggleListening = () => {
    if (!SpeechRecognition) {
      setError(t.modal.micUnsupported);
      return;
    }
    setError('');

    if (listening) {
      try { recognitionRef.current?.stop(); } catch { /* ignored */ }
      setListening(false);
      return;
    }

    const rec = new SpeechRecognition();
    rec.lang = SPEECH_LANG[activeLang] || 'en-US';
    rec.continuous = true;
    rec.interimResults = true;

    baseTextRef.current = reflection ? reflection.trim() + ' ' : '';
    interimRef.current = '';

    rec.onresult = (ev) => {
      let interim = '';
      let finalText = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const transcript = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) {
          finalText += transcript + ' ';
        } else {
          interim += transcript;
        }
      }
      if (finalText) {
        baseTextRef.current = (baseTextRef.current + finalText).replace(/\s+/g, ' ');
        interimRef.current = '';
      } else {
        interimRef.current = interim;
      }
      setReflection((baseTextRef.current + interimRef.current).trim());
    };

    rec.onerror = () => { setListening(false); };
    rec.onend = () => { setListening(false); };

    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (_) {
      setListening(false);
    }
  };

  const handleSubmit = async () => {
    if (!reflection.trim()) {
      setError(t.modal.submitErrorEmpty);
      return;
    }
    setSubmitting(true);
    setError('');
    try { recognitionRef.current?.stop(); } catch { /* ignored */ }

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (isAuthenticated) Object.assign(headers, authHeaders());
      const res = await fetch(`${backendUrl}/api/book-whispers`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          book_title: bookTitle.trim() || null,
          reflection: reflection.trim(),
          name: name.trim() || null,
          language: activeLang,
          cover_image_data_url: coverFile?.dataUrl || null,
        }),
      });
      if (!res.ok) throw new Error('failed');

      // Sealing animation, then confirmation
      setPhase('sealing');
      setTimeout(() => setPhase('done'), 850);
    } catch (_) {
      setError(t.modal.submitErrorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="bw-section" id="book-whispers" aria-labelledby="bw-title">
        <div className="bw-section-inner">
          <motion.h2
            id="bw-title"
            className="bw-title"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {(() => {
              const parts = (t.sectionTitle || '').trim().split(' ');
              if (parts.length < 2) return t.sectionTitle;
              const last = parts.pop();
              return (
                <>
                  {parts.join(' ')}{' '}
                  <span className="bw-title-accent">{last}</span>
                </>
              );
            })()}
          </motion.h2>
          <motion.h3
            className="bw-subtitle"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.06 }}
          >
            {t.sectionSubtitle}
          </motion.h3>
          <motion.p
            className="bw-tagline"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.12 }}
          >
            {t.sectionTagline.includes(' — ')
              ? t.sectionTagline.split(' — ').map((part, i, arr) => (
                  <React.Fragment key={`bw-tagline-${i}-${part.slice(0, 8)}`}>
                    {part}
                    {i < arr.length - 1 && (<><br /><span className="bw-tagline-em">— </span></>)}
                  </React.Fragment>
                ))
              : t.sectionTagline}
          </motion.p>

          <span className="bw-divider" aria-hidden="true">
            <span className="bw-divider-line" />
            <span className="bw-divider-mark">❦</span>
            <span className="bw-divider-line" />
          </span>

          <motion.button
            type="button"
            className="bw-cta-stamp"
            data-testid="bw-cta-open"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.2 }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            <span className="bw-cta-ring" aria-hidden="true" />
            <span className="bw-cta-label">{t.cta}</span>
          </motion.button>
        </div>
      </section>

      <AnimatePresence>
        {open && (
          <motion.div
            className="bw-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeModal}
            data-testid="bw-overlay"
          >
            <motion.div
              className={`bw-scroll ${phase === 'sealing' ? 'is-sealing' : ''}`}
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              exit={{ scaleX: 0, opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              {/* Decorative wooden rods */}
              <span className="bw-rod bw-rod-top" aria-hidden="true" />
              <span className="bw-rod bw-rod-bottom" aria-hidden="true" />

              {/* Close */}
              <button
                type="button"
                className="bw-close"
                onClick={closeModal}
                aria-label={t.modal.close}
                data-testid="bw-close"
              >
                <X size={18} />
              </button>

              <div className="bw-parchment">
                {phase !== 'done' && (
                  <motion.div
                    className="bw-form-wrap"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: phase === 'sealing' ? 0.55 : 1 }}
                    transition={{ duration: 0.35, delay: phase === 'form' ? 0.3 : 0 }}
                  >
                    <div className="bw-grid">
                      {/* LEFT — book identity */}
                      <div className="bw-left">
                        <h4 className="bw-side-heading">{t.modal.leftHeading}</h4>

                        <label className="bw-input-wrap">
                          <BookMarked size={16} className="bw-input-icon" />
                          <input
                            type="text"
                            className="bw-input"
                            placeholder={t.modal.searchPlaceholder}
                            value={bookTitle}
                            onChange={(e) => setBookTitle(e.target.value)}
                            data-testid="bw-book-title"
                            maxLength={300}
                          />
                        </label>
                        <p className="bw-input-hint">{t.modal.searchHint}</p>

                        <div className="bw-upload">
                          <div className="bw-upload-head">
                            <Feather size={16} className="bw-upload-icon" />
                            <div>
                              <p className="bw-upload-label">{t.modal.uploadLabel}</p>
                              <p className="bw-upload-hint">{t.modal.uploadHint}</p>
                            </div>
                          </div>
                          {!coverFile ? (
                            <label className="bw-upload-cta" data-testid="bw-upload-cta">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleCoverChange}
                                data-testid="bw-upload-input"
                                style={{ display: 'none' }}
                              />
                              {t.modal.uploadCta}
                            </label>
                          ) : (
                            <div className="bw-upload-attached">
                              <img src={coverFile.dataUrl} alt="cover" className="bw-upload-thumb" />
                              <div className="bw-upload-attached-text">
                                <span className="bw-upload-attached-prefix">{t.modal.uploadedPrefix}</span>
                                <span className="bw-upload-attached-name">{coverFile.name}</span>
                              </div>
                              <button
                                type="button"
                                className="bw-upload-remove"
                                onClick={() => setCoverFile(null)}
                                data-testid="bw-upload-remove"
                              >
                                {t.modal.uploadRemove}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Vertical ornamental divider */}
                      <div className="bw-vdivider" aria-hidden="true">
                        <span className="bw-vdivider-line" />
                        <span className="bw-vdivider-mark">❦</span>
                        <span className="bw-vdivider-line" />
                      </div>

                      {/* RIGHT — reflection */}
                      <div className="bw-right">
                        <h4 className="bw-side-heading">{t.modal.rightHeading}</h4>

                        <div className="bw-mic-wrap">
                          <button
                            type="button"
                            className={`bw-mic ${listening ? 'is-listening' : ''}`}
                            onClick={toggleListening}
                            data-testid="bw-mic"
                            aria-pressed={listening}
                          >
                            {/* ink-ripple rings while listening */}
                            <span className="bw-mic-ripple bw-mic-ripple-1" aria-hidden="true" />
                            <span className="bw-mic-ripple bw-mic-ripple-2" aria-hidden="true" />
                            <Mic size={26} strokeWidth={2.2} />
                          </button>
                          <p className="bw-mic-label">
                            {listening ? t.modal.micListening : t.modal.micLabel}
                          </p>
                          <p className="bw-mic-hint">{t.modal.micHint}</p>
                        </div>

                        <label className="bw-textarea-label" htmlFor="bw-textarea">
                          {t.modal.textAreaLabel}
                        </label>
                        <textarea
                          id="bw-textarea"
                          className="bw-textarea"
                          placeholder={t.modal.textAreaPlaceholder}
                          value={reflection}
                          onChange={(e) => setReflection(e.target.value)}
                          rows={6}
                          maxLength={2000}
                          data-testid="bw-reflection"
                        />
                        <CharCount value={reflection} max={2000} testId="bw-reflection-count" />

                        <label className="bw-name-label" htmlFor="bw-name">
                          {t.modal.nameLabel}
                        </label>
                        <input
                          id="bw-name"
                          type="text"
                          className="bw-input bw-name-input"
                          placeholder={t.modal.namePlaceholder}
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          maxLength={120}
                          data-testid="bw-name"
                        />
                      </div>
                    </div>

                    {error && (
                      <p className="bw-error" data-testid="bw-error" role="alert">{error}</p>
                    )}

                    <div className="bw-submit-wrap">
                      <button
                        type="button"
                        className="bw-submit"
                        onClick={handleSubmit}
                        disabled={submitting}
                        data-testid="bw-submit"
                        style={{ backgroundImage: "url('/seal/icraftz-wax-seal.png')" }}
                      >
                        <span className="bw-submit-scrim" aria-hidden="true" />
                        <span className="bw-submit-label">
                          {submitting ? t.modal.submitting : t.modal.submit}
                        </span>
                      </button>
                      <p className="bw-bottom-tagline">{t.modal.bottomTagline}</p>
                    </div>
                  </motion.div>
                )}

                {phase === 'done' && (
                  <motion.div
                    className="bw-confirm"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45 }}
                    data-testid="bw-confirm"
                  >
                    <img
                      src="/seal/icraftz-wax-seal.png"
                      alt="iCraftz wax seal"
                      className="bw-wax-seal-img"
                      data-testid="bw-wax-seal-img"
                    />
                    <h3 className="bw-confirm-heading">{t.confirmation.heading}</h3>
                    <p className="bw-confirm-subtitle">{t.confirmation.subtitle}</p>
                    <span className="bw-divider bw-divider-sm" aria-hidden="true">
                      <span className="bw-divider-line" />
                      <span className="bw-divider-mark">❦</span>
                      <span className="bw-divider-line" />
                    </span>
                    <p className="bw-confirm-signoff">{t.confirmation.signoff}</p>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
